import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../../../common/element/Table";
import Checkbox from "../../../../../../common/element/Checkbox";
import BtnLight from "../../../../../../common/element/Buttons/btnLight";
import BtnPrimary from "../../../../../../common/element/Buttons/btnPrimary";
import Dialog from "../../../../../../common/element/Dialog/Dialog";
import ThreeDotBouncing from "../../../../../../common/element/PageLoader";
import moment from "moment";
import { AdminGetAllAssignedChecklistOfWorker, AdminUpdateWorkerChecklistAssign } from "../../../../../../actions/admin-provider/worker/GetAllAssignedChecklistOfWorker";

const AssignedChecklist = ({ individual_id, clientId, onClose }) => {
  const dispatch = useDispatch();
  const { Tr, Th, Td, THead, TBody } = Table;
  const { SpecificProviderData } = useSelector((state) => state);

  const [loadData, setLoadData] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [dialogAddOpen, setDialogAddOpen] = useState(false);
  const [workerCompCheckList, setWorkerCompCheckList] = useState([]);
  const [checkedItems, setCheckedItems] = useState([]);
  const [checklistIds, setChecklistIds] = useState([]);
  const [removeChecklistIds, setRemoveChecklistIds] = useState([]);
  const [sortState, setSortState] = useState({ sort: "checklist_name", order: "asc" });

  const OnGetAllAssignedChecklistOfWorker = useCallback(async () => {
    try {
      setLoadData(false);
      const resolvedClientOrgId = clientId?.organisation_id || individual_id?.client_assigns?.[0]?.organisation_id;
      const payload = {
        client_org_id: resolvedClientOrgId,
        worker_id: individual_id?.worker_id,
      };
      const response = await dispatch(AdminGetAllAssignedChecklistOfWorker(payload));

      console.log("Data", response)
      const { success, data } = response || {};
      if (success) {
        const arrayData = data?.data || [];
        setWorkerCompCheckList(arrayData);
        const alreadyAssignedChecklist = arrayData.filter(
          (ele) => ele?.WorkerAssignments && ele?.WorkerAssignments.length > 0
        );
        setCheckedItems(alreadyAssignedChecklist);
      }
    } catch (error) {
      // no-op
    } finally {
      setLoadData(true);
    }
  }, [dispatch, clientId?.organisation_id, individual_id?.worker_id, individual_id?.client_assigns]);

  useEffect(() => {
    OnGetAllAssignedChecklistOfWorker();
  }, [OnGetAllAssignedChecklistOfWorker]);

  const onCheckboxChange = (isChecked, item) => {
    const existingIndex = checkedItems.findIndex(
      (ele) => ele.checklist_id === item.checklist_id
    );

    if (isChecked && existingIndex === -1) {
      setCheckedItems((prevItems) => [...prevItems, item]);
      setChecklistIds((prevIds) => [...prevIds, item.checklist_id]);
      setRemoveChecklistIds((prevIds) =>
        prevIds.filter((id) => id !== item.checklist_id)
      );
    } else if (!isChecked && existingIndex !== -1) {
      setCheckedItems((prevItems) => {
        const updatedItems = [...prevItems];
        updatedItems.splice(existingIndex, 1);
        return updatedItems;
      });
      setRemoveChecklistIds((prevIds) => [...prevIds, item.checklist_id]);
      setChecklistIds((prevIds) =>
        prevIds.filter((id) => id !== item.checklist_id)
      );
    }
  };

  const onCloseConfirmDialog = () => setOpenConfirmDialog(false);
  const onAddDialogClose = () => setDialogAddOpen(false);

  const handleSort = (column) => {
    setSortState((prev) => ({
      sort: column,
      order: prev.sort === column && prev.order === "asc" ? "desc" : "asc",
    }));
  };

  const sortedList = useMemo(() => {
    const arr = [...(workerCompCheckList || [])];
    const { sort, order } = sortState;
    const dir = order === "asc" ? 1 : -1;
    return arr.sort((a, b) => {
      let aVal;
      let bVal;
      if (sort === "checklist_name") {
        aVal = (a?.checklist_name || "").toString().toLowerCase();
        bVal = (b?.checklist_name || "").toString().toLowerCase();
        if (aVal < bVal) return -1 * dir;
        if (aVal > bVal) return 1 * dir;
        return 0;
      }
      if (sort === "checklist_renewal_date") {
        const aTime = a?.checklist_renewal_date ? new Date(a.checklist_renewal_date).getTime() : null;
        const bTime = b?.checklist_renewal_date ? new Date(b.checklist_renewal_date).getTime() : null;
        if (aTime === null && bTime === null) return 0;
        if (aTime === null) return 1; // nulls last
        if (bTime === null) return -1;
        return (aTime - bTime) * dir;
      }
      return 0;
    });
  }, [workerCompCheckList, sortState]);

  const onConfirm = () => {
    const payload = {
      assignChecklistIds: JSON.stringify(checklistIds),
      removeChecklistIds: JSON.stringify(removeChecklistIds),
      provider_org_id: SpecificProviderData?.organisation_id,
      client_org_id: clientId?.organisation_id || individual_id?.client_assigns?.[0]?.organisation_id,
      worker_id: individual_id?.worker_id,
    };
    dispatch(AdminUpdateWorkerChecklistAssign(payload))
      .then((res) => {
        if (res?.success === true) {
          setOpenConfirmDialog(false);
          setDialogAddOpen(true);
          setRemoveChecklistIds([]);
          setCheckedItems([]);
          setChecklistIds([]);
          OnGetAllAssignedChecklistOfWorker();
        }
      })
      .catch(() => {});
  };

  return (
    <React.Fragment>
      <div className="border rounded-lg border-gray-200 m-2">
        <div className="h-max overflow-auto">
          <Table>
            <THead>
              <Tr>
                <Th>
                  <span
                    className="flex items-center gap-1 cursor-pointer select-none"
                    onClick={() => handleSort("checklist_name")}
                  >
                    Checklist
                    <svg
                      className={`transform ${sortState.sort === "checklist_name" && sortState.order === "asc" ? "rotate-180" : ""}`}
                      width="17"
                      height="16"
                      viewBox="0 0 17 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8.712 3.333v9.334m0 0L13.38 8m-4.667 4.667L4.045 8"
                        stroke="#475467"
                        strokeWidth="1.333"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </Th>
                <Th>
                  <span
                    className="flex items-center gap-1 justify-center cursor-pointer select-none"
                    onClick={() => handleSort("checklist_renewal_date")}
                  >
                    Expiry date
                    <svg
                      className={`transform ${sortState.sort === "checklist_renewal_date" && sortState.order === "asc" ? "rotate-180" : ""}`}
                      width="17"
                      height="16"
                      viewBox="0 0 17 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8.712 3.333v9.334m0 0L13.38 8m-4.667 4.667L4.045 8"
                        stroke="#475467"
                        strokeWidth="1.333"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </Th>
              </Tr>
            </THead>
            <TBody>
              {sortedList?.length > 0 &&
                sortedList.map((item) => (
                  <Tr key={item.checklist_id}>
                    <Td className="flex gap-2">
                      <Checkbox
                        className="cursor-pointer"
                        checked={checkedItems.some(
                          (checkedItem) =>
                            checkedItem.checklist_id === item.checklist_id
                        )}
                        onChange={(e) => onCheckboxChange(e, item)}
                      />
                      {item?.checklist_name ? item?.checklist_name : "-"}
                    </Td>
                    <Td className="text-center">
                      {item.checklist_renewal_date
                        ? moment(item?.checklist_renewal_date).format(
                            "DD/MM/YYYY"
                          )
                        : "-"}
                    </Td>
                  </Tr>
                ))}
            </TBody>
          </Table>
          {!loadData && (
            <div>
              <ThreeDotBouncing />
            </div>
          )}
          {loadData && workerCompCheckList?.length === 0 && (
            <div className="h-full w-full text-center mt-2">No data available</div>
          )}
        </div>
      </div>

      <div className="flex dailog-footer ">
        <BtnLight
          className="mr-1.5"
          variant="plain"
          onClick={() => {
            if (onClose) {
              onClose();
              return;
            }
            setCheckedItems([]);
            setChecklistIds([]);
            setRemoveChecklistIds([]);
            OnGetAllAssignedChecklistOfWorker();
          }}
        >
          Cancel
        </BtnLight>
        <BtnPrimary
          type="submit"
          onClick={() => setOpenConfirmDialog(true)}
          className="ml-1.5"
        >
          Save updates
        </BtnPrimary>
      </div>

      <Dialog
        isOpen={openConfirmDialog}
        onClose={onCloseConfirmDialog}
        onRequestClose={onCloseConfirmDialog}
        bodyOpenClassName="overflow-hidden"
        className="customAlert"
      >
        <div className="px-6 pt-6">
          <span className="w-12 h-12 rounded-full m-auto bg-success-100 flex items-center justify-center">
            <svg
              width="49"
              height="48"
              viewBox="0 0 49 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="1.01331" y="0.5" width="47" height="47" rx="23.5" fill="#D2EBFF" />
              <rect x="1.01331" y="0.5" width="47" height="47" rx="23.5" stroke="#ECFDF3" />
              <path
                d="M26.5133 14.2695V18.4001C26.5133 18.9601 26.5133 19.2401 26.6223 19.4541C26.7182 19.6422 26.8712 19.7952 27.0593 19.8911C27.2732 20.0001 27.5533 20.0001 28.1133 20.0001H32.2438M22.3633 24.0022C22.5395 23.5014 22.8873 23.079 23.345 22.81C23.8028 22.5409 24.341 22.4426 24.8643 22.5324C25.3876 22.6221 25.8623 22.8942 26.2042 23.3004C26.5462 23.7066 26.7333 24.2207 26.7325 24.7517C26.7325 26.2506 24.4842 27 24.4842 27M24.5133 30H24.5233M26.5133 14H21.3133C19.6331 14 18.7931 14 18.1513 14.327C17.5868 14.6146 17.1279 15.0735 16.8403 15.638C16.5133 16.2798 16.5133 17.1198 16.5133 18.8V29.2C16.5133 30.8802 16.5133 31.7202 16.8403 32.362C17.1279 32.9265 17.5868 33.3854 18.1513 33.673C18.7931 34 19.6331 34 21.3133 34H27.7133C29.3935 34 30.2335 34 30.8753 33.673C31.4398 33.3854 31.8987 32.9265 32.1863 32.362C32.5133 31.7202 32.5133 30.8802 32.5133 29.2V20L26.5133 14Z"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <p className="text-base text-gray-900 font-semibold text-center mt-4">
            Are you sure you want to make these updates?
          </p>
          <p className="text-gray-600 text-center">
            If additional checklists are assigned, Workers will need their documents reviewed again.
          </p>
        </div>
        <div className="mt-8 flex justify-between pb-6 px-6">
          <BtnLight onClick={onCloseConfirmDialog}>Close</BtnLight>
          <BtnPrimary onClick={onConfirm}>Confirm</BtnPrimary>
        </div>
      </Dialog>

      <Dialog
        isOpen={dialogAddOpen}
        onClose={onAddDialogClose}
        onRequestClose={onAddDialogClose}
        bodyOpenClassName="overflow-hidden"
        className="customAlert"
      >
        <div className="px-6 pt-6">
          <span className="w-12 h-12 rounded-full m-auto bg-success-100 flex items-center justify-center">
            <svg
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.5 2.27V6.4c0 .56 0 .84.109 1.054a1 1 0 0 0 .437.437c.214.11.494.11 1.054.11h4.13M9.5 16l2 2 4.5-4.5M14.5 2H9.3c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C4.5 4.28 4.5 5.12 4.5 6.8v10.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C6.78 22 7.62 22 9.3 22h6.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311c.327-.642.327-1.482.327-3.162V8l-6-6z"
                stroke="#344054"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <p className="text-base text-gray-900 font-semibold text-center mt-4">
            Checklist updated
          </p>
        </div>
        <div className="mt-8 flex justify-between pb-6 px-6">
          <BtnLight onClick={onAddDialogClose}>Close</BtnLight>
        </div>
      </Dialog>
    </React.Fragment>
  );
};

export default AssignedChecklist;
